import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ProfitableIcon,
  Cancel as UnprofitableIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

const COLORS = ['#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

const Financials = () => {
  const [overview, setOverview] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [dailyCosts, setDailyCosts] = useState(null);
  const [userCosts, setUserCosts] = useState(null);
  const [staticCosts, setStaticCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  const [staticCostDialog, setStaticCostDialog] = useState(false);
  const [editingStaticCost, setEditingStaticCost] = useState(null);
  const [staticCostForm, setStaticCostForm] = useState({
    category: 'hosting',
    name: '',
    description: '',
    monthlyCost: '',
  });

  const loadOverview = useCallback(async () => {
    try {
      const res = await adminAPI.getFinancialsOverview({ year: selectedYear, month: selectedMonth });
      setOverview(res.data);
    } catch (err) {
      console.error('Failed to load overview:', err);
      setError('Failed to load financial overview');
    }
  }, [selectedYear, selectedMonth]);

  const loadCostBreakdown = useCallback(async () => {
    try {
      const res = await adminAPI.getCostBreakdown({ year: selectedYear, month: selectedMonth });
      setCostBreakdown(res.data);
    } catch (err) {
      console.error('Failed to load cost breakdown:', err);
    }
  }, [selectedYear, selectedMonth]);

  const loadDailyCosts = useCallback(async () => {
    try {
      const res = await adminAPI.getDailyCosts({ days: 30 });
      setDailyCosts(res.data);
    } catch (err) {
      console.error('Failed to load daily costs:', err);
    }
  }, []);

  const loadUserCosts = useCallback(async () => {
    try {
      const res = await adminAPI.getCostsByUser({ year: selectedYear, month: selectedMonth, limit: 50 });
      setUserCosts(res.data);
    } catch (err) {
      console.error('Failed to load user costs:', err);
    }
  }, [selectedYear, selectedMonth]);

  const loadStaticCosts = useCallback(async () => {
    try {
      const res = await adminAPI.getStaticCosts();
      setStaticCosts(res.data);
    } catch (err) {
      console.error('Failed to load static costs:', err);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    await Promise.all([
      loadOverview(),
      loadCostBreakdown(),
      loadDailyCosts(),
      loadUserCosts(),
      loadStaticCosts(),
    ]);
    setLoading(false);
  }, [loadOverview, loadCostBreakdown, loadDailyCosts, loadUserCosts, loadStaticCosts]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadOverview();
    loadCostBreakdown();
    loadUserCosts();
  }, [selectedYear, selectedMonth, loadOverview, loadCostBreakdown, loadUserCosts]);

  const handleSaveStaticCost = async () => {
    try {
      if (editingStaticCost) {
        await adminAPI.updateStaticCost(editingStaticCost.id, staticCostForm);
      } else {
        await adminAPI.createStaticCost(staticCostForm);
      }
      setStaticCostDialog(false);
      setEditingStaticCost(null);
      setStaticCostForm({ category: 'hosting', name: '', description: '', monthlyCost: '' });
      loadStaticCosts();
      loadOverview();
    } catch (err) {
      console.error('Failed to save static cost:', err);
      alert('Failed to save static cost');
    }
  };

  const handleDeleteStaticCost = async (id) => {
    if (!window.confirm('Delete this static cost?')) return;
    try {
      await adminAPI.deleteStaticCost(id);
      loadStaticCosts();
      loadOverview();
    } catch (err) {
      console.error('Failed to delete static cost:', err);
      alert('Failed to delete static cost');
    }
  };

  const openEditDialog = (cost) => {
    setEditingStaticCost(cost);
    setStaticCostForm({
      category: cost.category,
      name: cost.name,
      description: cost.description || '',
      monthlyCost: cost.monthlyCost.toString(),
    });
    setStaticCostDialog(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
  ];

  const years = [2025, 2026, 2027];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Financials</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Month</InputLabel>
            <Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>
              {months.map((m) => (<MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
              {years.map((y) => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={loadAll} variant="outlined">Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {overview && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Revenue</Typography>
                    <Typography variant="h4" color="success.main">${overview.revenue.collected.toFixed(2)}</Typography>
                  </Box>
                  <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
                <Box mt={1} display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                  <Chip label={`MRR: $${overview.revenue.mrr.toFixed(2)}`} size="small" color="success" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">{overview.revenue.activeSubscriptions} active</Typography>
                  {overview.revenue.trialingSubscriptions > 0 && (
                    <Typography variant="caption" color="warning.main">{overview.revenue.trialingSubscriptions} trialing</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Carrier Costs</Typography>
                    <Typography variant="h4">${overview.costs.carrier.total.toFixed(2)}</Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                </Box>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  <Chip icon={<PhoneIcon />} label={`$${overview.costs.carrier.voice.toFixed(2)}`} size="small" />
                  <Chip icon={<SmsIcon />} label={`$${overview.costs.carrier.sms.toFixed(2)}`} size="small" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Total Costs</Typography>
                    <Typography variant="h4">${overview.costs.total.toFixed(2)}</Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                </Box>
                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    Carrier: ${overview.costs.carrier.total.toFixed(2)} + Static: ${overview.costs.static.total.toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ bgcolor: overview.margin.amount >= 0 ? 'success.50' : 'error.50' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Margin</Typography>
                    <Typography variant="h4" color={overview.margin.amount >= 0 ? 'success.main' : 'error.main'}>
                      ${overview.margin.amount.toFixed(2)}
                    </Typography>
                  </Box>
                  {overview.margin.amount >= 0 ? <ProfitableIcon color="success" sx={{ fontSize: 40 }} /> : <UnprofitableIcon color="error" sx={{ fontSize: 40 }} />}
                </Box>
                <Box mt={1}><Chip label={`${overview.margin.percent}%`} size="small" color={overview.margin.amount >= 0 ? 'success' : 'error'} /></Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Cost Breakdown" />
          <Tab label="Daily Trend" />
          <Tab label="Per-User Costs" />
          <Tab label="Static Costs" />
        </Tabs>

        {activeTab === 0 && costBreakdown && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Cost by Type</Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Avg/Transaction</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {costBreakdown.breakdown.map((row) => (
                        <TableRow key={row.type}>
                          <TableCell><Chip label={row.type.replace('_', ' ')} size="small" color={row.type.includes('voice') ? 'primary' : 'secondary'} variant="outlined" /></TableCell>
                          <TableCell align="right">${row.totalCost.toFixed(2)}</TableCell>
                          <TableCell align="right">{row.transactionCount}</TableCell>
                          <TableCell align="right">${row.avgCostPerTransaction.toFixed(4)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Cost Distribution</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={costBreakdown.breakdown} dataKey="totalCost" nameKey="type" cx="50%" cy="50%" outerRadius={100} label={({ name, value }) => `${name}: $${value.toFixed(2)}`}>
                      {costBreakdown.breakdown.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeTab === 1 && dailyCosts && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Daily Costs (Last 30 Days)</Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dailyCosts.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(d) => format(new Date(d), 'MM/dd')} />
                <YAxis tickFormatter={(v) => `$${v}`} />
                <RechartsTooltip formatter={(value) => [`$${value.toFixed(2)}`, '']} labelFormatter={(d) => format(new Date(d), 'MMM dd, yyyy')} />
                <Legend />
                <Bar dataKey="voice" fill="#7c3aed" name="Voice" stackId="a" />
                <Bar dataKey="sms" fill="#ec4899" name="SMS" stackId="a" />
                <Bar dataKey="recording" fill="#f59e0b" name="Recording" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {activeTab === 2 && userCosts && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6">Per-User Cost Analysis</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip icon={<ProfitableIcon />} label={`${userCosts.summary.profitableUsers} Profitable`} color="success" />
                <Chip icon={<UnprofitableIcon />} label={`${userCosts.summary.unprofitableUsers} Unprofitable`} color="error" />
              </Box>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Average cost per user: <strong>${userCosts.summary.avgCostPerUser.toFixed(2)}</strong> | Total MRR: <strong>${userCosts.summary.totalMRR.toFixed(2)}</strong>
            </Alert>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Events</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Voice</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>SMS</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>MRR</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Margin</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userCosts.users.map((user) => (
                    <TableRow key={user.userId} sx={{ bgcolor: user.profitable ? 'inherit' : 'error.50' }}>
                      <TableCell>
                        <Typography variant="body2">{user.email}</Typography>
                        {user.name && <Typography variant="caption" color="text.secondary">{user.name}</Typography>}
                      </TableCell>
                      <TableCell align="right">{user.eventCount}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>${user.costs.voice.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>${user.costs.sms.toFixed(2)}</TableCell>
                      <TableCell align="right">${user.costs.total.toFixed(2)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {user.subscription.trialing
                          ? <Chip label="Trialing" size="small" color="warning" />
                          : user.subscription.active
                            ? <Typography color="success.main">
                                ${user.subscription.mrr.toFixed(2)}
                                {user.subscription.discounted && <Typography component="span" variant="caption" color="warning.main"> (disc.)</Typography>}
                              </Typography>
                            : <Chip label="Free" size="small" />}
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Typography color={user.margin >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">${user.margin.toFixed(2)}</Typography></TableCell>
                      <TableCell align="center">{user.profitable ? <ProfitableIcon color="success" /> : <UnprofitableIcon color="error" />}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeTab === 3 && staticCosts && (
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Static Monthly Costs</Typography>
                <Chip label={`Total: $${staticCosts.totalMonthly.toFixed(2)}/mo`} color="primary" />
              </Box>
              <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => { setEditingStaticCost(null); setStaticCostForm({ category: 'hosting', name: '', description: '', monthlyCost: '' }); setStaticCostDialog(true); }}>
                Add Cost
              </Button>
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                    <TableCell align="right">Monthly Cost</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Effective From</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staticCosts.costs.map((cost) => (
                    <TableRow key={cost.id}>
                      <TableCell><Chip label={cost.category} size="small" variant="outlined" /></TableCell>
                      <TableCell>{cost.name}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Typography variant="body2" color="text.secondary">{cost.description || '-'}</Typography></TableCell>
                      <TableCell align="right">${cost.monthlyCost.toFixed(2)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{cost.effectiveFrom ? format(new Date(cost.effectiveFrom), 'MMM dd, yyyy') : '-'}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => openEditDialog(cost)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteStaticCost(cost.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {staticCosts.costs.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary">No static costs configured.</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Dialog open={staticCostDialog} onClose={() => setStaticCostDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingStaticCost ? 'Edit Static Cost' : 'Add Static Cost'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={staticCostForm.category} label="Category" onChange={(e) => setStaticCostForm({ ...staticCostForm, category: e.target.value })}>
                <MenuItem value="hosting">Hosting</MenuItem>
                <MenuItem value="api_subscription">API Subscription</MenuItem>
                <MenuItem value="software">Software</MenuItem>
                <MenuItem value="domain">Domain</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Name" fullWidth value={staticCostForm.name} onChange={(e) => setStaticCostForm({ ...staticCostForm, name: e.target.value })} placeholder="e.g., Railway Backend Hosting" />
            <TextField label="Description" fullWidth multiline rows={2} value={staticCostForm.description} onChange={(e) => setStaticCostForm({ ...staticCostForm, description: e.target.value })} placeholder="Optional description" />
            <TextField label="Monthly Cost (USD)" fullWidth type="number" inputProps={{ step: 0.01, min: 0 }} value={staticCostForm.monthlyCost} onChange={(e) => setStaticCostForm({ ...staticCostForm, monthlyCost: e.target.value })} placeholder="0.00" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStaticCostDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveStaticCost} disabled={!staticCostForm.name || !staticCostForm.monthlyCost}>{editingStaticCost ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Financials;
